import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import TesterDetail from './TesterDetail';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import Button from '../Button/Button';
import useMode from '../../hooks/useMode';
import { PortalWithState } from 'react-portal';
import TesterEditModal from './TesterEditModal';
import useNotify from '../../hooks/useNotify';
import { Tester } from './types';
import { ApolloError } from '@apollo/client/errors';
import { GET_COMPANY_TESTER_DETAIL } from './TesterThirdPane.queries.company';
import { GET_WATER_SUPPLIER_TESTER_DETAIL } from './TesterThirdPane.queries.water-supplier';
import { gql as company_gql } from '@/modes/company/gql';
import RequirePerms from '@/components/RequirePerms';

const SET_VENDOR_STATUS = company_gql(`#graphql
  mutation SetVendorStatus($vendorId: ID!, $status: ContractStatus!) {
    setVendorStatus(vendorId: $vendorId, status: $status) {
      id
      status
    }
  }
`);

type TesterThirdPaneProps = {
  vendorId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function TesterThirdPane({
  vendorId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: TesterThirdPaneProps): JSX.Element {
  const mode = useMode();

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery(
    mode === 'water-supplier'
      ? GET_WATER_SUPPLIER_TESTER_DETAIL
      : GET_COMPANY_TESTER_DETAIL,
    {
      skip: !vendorId,
      variables: { where: { id: vendorId || null } },
      onError: useCallback(
        (error: ApolloError) => {
          showError(`Error loading vendor ${vendorId}`, error);
        },
        [vendorId]
      ),
    }
  );

  const { showError } = useNotify();

  const vendor = itemData?.vendors?.[0] as any;

  const [setVendorStatus] = useMutation(SET_VENDOR_STATUS, {
    onError: useCallback(
      (error: ApolloError) => {
        showError('Error setting vendor status', error);
      },
      [showError]
    ),
  });

  const activateDeactivate = () => {
    if (!vendor) {
      return;
    }
    if (vendor.status === 'ACTIVE') {
      setVendorStatus({
        variables: { vendorId: vendor.id, status: 'INACTIVE' },
      });
    } else {
      setVendorStatus({
        variables: { vendorId: vendor.id, status: 'ACTIVE' },
      });
    }
  };

  return (
    <ThirdPane width={550} visible={!!vendorId}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={vendor?.profile?.name || 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        <RequirePerms vendors="UPDATE">
          {vendor && (
            <Button
              mr={3}
              variant={vendor.status === 'ACTIVE' ? 'danger' : 'outline'}
              onClick={activateDeactivate}
            >
              {vendor.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
        </RequirePerms>
        {vendor && vendor.profile.perms.includes('UPDATE') && (
          <RequirePerms vendors="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <TesterEditModal
                      action="edit"
                      perms={vendor.profile.perms}
                      onClose={() => {
                        closePortal();
                        refetch();
                      }}
                      tester={vendor}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        )}
      </ThirdPaneHeader>
      <ThirdPaneContent>
        {vendor && (
          <TesterDetail testerDetail={vendor as Tester} refetch={refetch} />
        )}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
